import { FC, useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";

import { Button, CurrencyInput, InputType, Modal } from "../common";
import { CURRENCY_MAP } from "../../constants";
import { Currency } from "../../types/InvoiceTypes";
import {
  PaymentRequestLinks,
  PaymentRequestPriceData,
} from "../../types/PaymentRequestLinkTypes";
import { formatAmount } from "../../utils/numbers/money";

type Props = {
  allowedIncomingCurrencies: Currency[];
  allowedOutgoingCurrencies: Currency[];
  maxIncomingAmount?: number;
  priceData?: PaymentRequestLinks | PaymentRequestPriceData;
  ctaLabel?: string;
  onClickCTA?: () => void;
  disabledTryItOut?: boolean;
  onChange?: (input: Partial<InputType>) => void;
};

export const TransferCalculator: FC<Props> = ({
  allowedIncomingCurrencies,
  allowedOutgoingCurrencies,
  maxIncomingAmount,
  priceData,
  ctaLabel = "Receive Money",
  onClickCTA,
  disabledTryItOut = false,
  onChange,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const [calculatorInput, setCalculatorInput] = useState<InputType>({
    currency: Currency.EUR,
    amount: 100,
  });

  const [calculatorOutput, setCalculatorOutput] = useState<InputType>({
    currency: Currency.INR,
    amount: 0,
  });

  const calculatorInputRef = useRef<InputType | null>(calculatorInput);

  const { symbol } = CURRENCY_MAP[calculatorOutput.currency];

  const tds = (priceData?.tds || 0) * Number(calculatorOutput.amount);

  const calculateRate = useCallback(
    (inputCurrency: Currency | undefined, commission: number) => {
      return (
        ((((priceData || {}) as any)[`${inputCurrency}_price`] as number) ||
          1) *
        (1 - commission)
      );
    },
    [priceData]
  );

  const getInitAmount = useCallback(
    (type: "incoming" | "outgoing", amount: number, rate: number) => {
      if (!maxIncomingAmount) {
        return amount;
      }

      if (type === "incoming") {
        return Math.min(amount, maxIncomingAmount);
      }

      // type === "outgoing"
      return amount / rate > maxIncomingAmount
        ? Number((maxIncomingAmount * rate).toFixed(2))
        : amount;
    },
    [maxIncomingAmount]
  );

  const updateCalculator = useCallback(
    (input: Partial<InputType>, type: "incoming" | "outgoing") => {
      const { amount, currency } = input;

      const inputCurrency =
        type === "incoming" ? currency : calculatorInput.currency;

      const commission = priceData?.commission || 0;
      const rate = calculateRate(inputCurrency, commission);
      const initialAmount = getInitAmount(type, Number(amount || 0), rate);

      const convertedAmount =
        type === "incoming"
          ? (initialAmount * rate).toFixed(2)
          : (initialAmount / rate).toFixed(2);

      setCalculatorInput((_) => ({
        currency: inputCurrency || Currency.EUR,
        amount: type === "incoming" ? initialAmount : convertedAmount,
      }));

      setCalculatorOutput((current) => ({
        ...current,
        amount: type === "incoming" ? convertedAmount : initialAmount,
      }));

      onChange &&
        onChange({
          currency: inputCurrency,
          amount: type === "incoming" ? initialAmount : convertedAmount,
        });
    },
    [
      calculatorInput.currency,
      onChange,
      priceData,
      calculateRate,
      getInitAmount,
    ]
  );

  useEffect(() => {
    if (priceData && calculatorInputRef.current) {
      updateCalculator(calculatorInputRef.current, "incoming");
      calculatorInputRef.current = null;
    }
  }, [priceData, updateCalculator]);

  const disabledAction =
    disabledTryItOut || !calculatorInput.amount || !calculatorOutput.amount;

  return (
    <div className="flex-1 flex flex-col gap-1">
      <CurrencyInput
        value={calculatorInput}
        message="Sent"
        onChange={(value) => updateCalculator(value, "incoming")}
        allowedCurrencies={allowedIncomingCurrencies}
      />

      <CurrencyInput
        value={calculatorOutput}
        message={
          <>
            Received, excluding TDS.{" "}
            <button
              className="text-green-600"
              onClick={() => setShowDetails(true)}
            >
              See details
            </button>
          </>
        }
        onChange={(value) => updateCalculator(value, "outgoing")}
        allowedCurrencies={allowedOutgoingCurrencies}
        variant={CurrencyInput.variant.GREEN}
        approx
        readonlyCurrency
      />

      {onClickCTA && (
        <Button
          className={"!rounded-2xl !justify-start"}
          contentClassName={classNames("text-left", {
            "text-white": !disabledAction,
          })}
          disabled={disabledAction}
          onClick={onClickCTA}
          variant={Button.variant.TEMPTING}
        >
          {ctaLabel}
        </Button>
      )}

      <Modal
        isOpen={showDetails}
        close={() => setShowDetails(false)}
        title="How much you'll get"
        subtitle="The exchange rate isn’t fixed, and the final amount will be based on the payment date. The numbers are not final"
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <div className="theme-subheading uppercase">
              In your bank account
            </div>

            <div className="">
              {symbol} {formatAmount(Number(calculatorOutput.amount) - tds)}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <div className="theme-subheading uppercase">
              At the end of year (TDS)
            </div>

            <div className="">
              {symbol} {formatAmount(tds)}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
